// SEO utilities for PrepFrame
// Marketing pages (prepframe.com) = indexable + optimized
// App pages (app.prepframe.com) = noindex, nofollow

import { isAppDomain } from './domainConfig';
import i18n from 'i18next';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '@/i18n/languages';

export interface SEOConfig {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  twitterCard?: 'summary' | 'summary_large_image';
  canonical?: string;
  noIndex?: boolean;
  /** SEO namespace key for translated meta (e.g. "landing", "pricing") */
  seoKey?: string;
}

const DEFAULT_SEO: SEOConfig = {
  title: 'PrepFrame - Photoshoot Brief Management for Photographers',
  description: 'Create beautiful client briefs, collect preferences before the shoot, and stay organized on shoot day. The all-in-one tool photographers love.',
  keywords: 'photography brief, photoshoot planning, client questionnaire, photographer tools, wedding photography, portrait photography, shoot preparation',
  ogTitle: 'PrepFrame - Photoshoot Brief Management',
  ogDescription: 'Create a photoshoot brief, share it with your client, and get all their preferences in one place before the shoot day.',
  ogImage: 'https://www.prepframe.com/og-image.jpg',
  ogType: 'website',
  twitterCard: 'summary_large_image',
};

const BASE_URL = 'https://www.prepframe.com';

/**
 * Build the URL for a given path and language
 */
function buildLangUrl(path: string, lang: string): string {
  const clean = path.replace(/\?.*$/, ''); // strip existing query
  if (lang === DEFAULT_LANGUAGE) return `${BASE_URL}${clean}`;
  return `${BASE_URL}${clean}?lng=${lang}`;
}

/**
 * Inject hreflang <link> tags for all supported languages
 */
function injectHreflangTags(canonicalPath: string): HTMLLinkElement[] {
  const links: HTMLLinkElement[] = [];

  for (const lang of SUPPORTED_LANGUAGES) {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = lang.code;
    link.href = buildLangUrl(canonicalPath, lang.code);
    document.head.appendChild(link);
    links.push(link);
  }

  // x-default → English (no query param)
  const xDefault = document.createElement('link');
  xDefault.rel = 'alternate';
  xDefault.hreflang = 'x-default';
  xDefault.href = `${BASE_URL}${canonicalPath.replace(/\?.*$/, '')}`;
  document.head.appendChild(xDefault);
  links.push(xDefault);

  return links;
}

/**
 * Update document head with SEO meta tags
 * Call this in useEffect on page mount
 */
export function updateSEO(config: SEOConfig = {}): () => void {
  const currentLang = i18n.language?.split('-')[0] || DEFAULT_LANGUAGE;

  // If a seoKey is provided, try to get translated title/description
  let translatedTitle: string | undefined;
  let translatedDesc: string | undefined;

  if (config.seoKey) {
    const tTitle = i18n.t(`seo:${config.seoKey}.title`, { defaultValue: '' });
    const tDesc = i18n.t(`seo:${config.seoKey}.description`, { defaultValue: '' });
    if (tTitle) translatedTitle = tTitle;
    if (tDesc) translatedDesc = tDesc;
  }

  const seo = {
    ...DEFAULT_SEO,
    ...config,
    ...(translatedTitle ? { title: translatedTitle, ogTitle: translatedTitle } : {}),
    ...(translatedDesc ? { description: translatedDesc, ogDescription: translatedDesc } : {}),
  };
  
  // App domain should never be indexed
  const shouldNoIndex = seo.noIndex || isAppDomain();
  
  // Store original values for cleanup
  const originalTitle = document.title;
  const createdElements: Element[] = [];
  
  // Helper to set or create meta tag
  const setMeta = (name: string, content: string, isProperty = false) => {
    const attr = isProperty ? 'property' : 'name';
    let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
    
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute(attr, name);
      document.head.appendChild(meta);
      createdElements.push(meta);
    }
    meta.content = content;
  };
  
  // Set title
  if (seo.title) {
    document.title = seo.title;
  }
  
  // Basic SEO meta tags
  if (seo.description) setMeta('description', seo.description);
  if (seo.keywords) setMeta('keywords', seo.keywords);
  
  // Robots directive
  setMeta('robots', shouldNoIndex ? 'noindex, nofollow' : 'index, follow');
  
  // Open Graph tags
  if (seo.ogTitle) setMeta('og:title', seo.ogTitle, true);
  if (seo.ogDescription) setMeta('og:description', seo.ogDescription, true);
  if (seo.ogImage) setMeta('og:image', seo.ogImage, true);
  if (seo.ogType) setMeta('og:type', seo.ogType, true);
  
  // Twitter Card tags
  if (seo.twitterCard) setMeta('twitter:card', seo.twitterCard);
  setMeta('twitter:site', '@PrepFrame');
  if (seo.ogTitle) setMeta('twitter:title', seo.ogTitle);
  if (seo.ogDescription) setMeta('twitter:description', seo.ogDescription);
  if (seo.ogImage) setMeta('twitter:image', seo.ogImage);
  
  // Canonical URL — self-referencing with language
  const canonicalPath = seo.canonical
    ? seo.canonical.replace(BASE_URL, '')
    : window.location.pathname;

  const selfCanonical = buildLangUrl(canonicalPath, currentLang);
  let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.rel = 'canonical';
    document.head.appendChild(canonicalLink);
    createdElements.push(canonicalLink);
  }
  canonicalLink.href = selfCanonical;

  // Hreflang tags (only for marketing / indexable pages)
  let hreflangLinks: HTMLLinkElement[] = [];
  if (!shouldNoIndex) {
    hreflangLinks = injectHreflangTags(canonicalPath);
  }

  // Set html lang attribute
  document.documentElement.lang = currentLang;
  
  // Cleanup function
  return () => {
    document.title = originalTitle;
    createdElements.forEach((el) => el.remove());
    hreflangLinks.forEach((el) => el.remove());
  };
}

// Pre-defined SEO configs for different pages
export const SEO_CONFIGS = {
  landing: {
    title: 'PrepFrame - Photoshoot Brief Management for Photographers',
    description: 'Create beautiful client briefs, collect preferences before the shoot, and stay organized on shoot day. The all-in-one tool photographers love.',
    canonical: 'https://www.prepframe.com/',
    seoKey: 'landing',
  },
  pricing: {
    title: 'PrepFrame Pricing: Plans for Photographers',
    description: 'Simple, transparent pricing for photographers. Start free, upgrade when you grow. No hidden fees, no contracts.',
    canonical: 'https://www.prepframe.com/pricing',
    seoKey: 'pricing',
  },
  demo: {
    title: 'See PrepFrame Demo: Interactive Photography Brief',
    description: 'See PrepFrame in action. Explore a sample photoshoot brief and field view to understand how PrepFrame helps photographers.',
    canonical: 'https://www.prepframe.com/demo',
    seoKey: 'demo',
  },
  privacy: {
    title: 'Privacy Policy - PrepFrame',
    description: 'Learn how PrepFrame protects your data. Our privacy policy explains what we collect, how we use it, and your rights.',
    canonical: 'https://www.prepframe.com/privacy',
    seoKey: 'privacy',
  },
  terms: {
    title: 'Terms of Service - PrepFrame',
    description: 'Read the PrepFrame terms of service. Understand your rights and responsibilities when using our platform.',
    canonical: 'https://www.prepframe.com/terms',
    seoKey: 'terms',
  },
  contact: {
    title: 'Contact PrepFrame: Support for Photographers',
    description: 'Get in touch with the PrepFrame team. We\'re here to help with questions, feedback, or support.',
    canonical: 'https://www.prepframe.com/contact',
    seoKey: 'contact',
  },
  clientBrief: {
    title: 'Photography Client Brief Link (No App Needed) | PrepFrame',
    description: 'Send your photography clients a simple link to share their preferences, must-have shots, and inspiration. No app downloads, no accounts. Just easy collaboration.',
    keywords: 'photography client brief, client questionnaire photography, photoshoot preferences form, wedding photography brief, client intake photographer',
    canonical: 'https://www.prepframe.com/client-brief',
    seoKey: 'clientBrief',
  },
  fieldView: {
    title: 'Shoot-day Field View & Moodboard | PrepFrame',
    description: 'Access your shoot plan, moodboard, and client must-haves on one mobile screen. Show up prepared to every photoshoot with Field View.',
    keywords: 'photography moodboard, shoot day checklist, photographer field guide, wedding photography planning, mobile moodboard app',
    canonical: 'https://www.prepframe.com/field-view',
    seoKey: 'fieldView',
  },
  projects: {
    title: 'Photography Project Organizer for Shoots | PrepFrame',
    description: 'Keep every photography project organized in one dashboard. Track status, manage client briefs, and archive completed shoots. No spreadsheets needed.',
    keywords: 'photography project management, photographer organization app, shoot tracker, photography workflow, client management photographer',
    canonical: 'https://www.prepframe.com/projects',
    seoKey: 'projects',
  },
  // Niche SEO landing pages
  weddingBrief: {
    title: 'Wedding Photography Brief | PrepFrame',
    ogTitle: 'Wedding Photography Brief | PrepFrame',
    description: 'Create a wedding photography brief your clients will actually fill out. Share must-have shots, family groupings, and style references before the big day.',
    keywords: 'wedding photography brief, wedding shot list, wedding photography questionnaire, bridal brief photographer',
    canonical: 'https://www.prepframe.com/wedding-photography-brief',
  },
  portraitBrief: {
    title: 'Portrait Photography Brief | PrepFrame',
    ogTitle: 'Portrait Photography Brief | PrepFrame',
    description: 'Send your portrait clients a pre-session brief. Collect outfit plans, location ideas, and style references before you meet. No guesswork on the day.',
    keywords: 'portrait photography brief, portrait session questionnaire, portrait client brief, pre-session brief photographer',
    canonical: 'https://www.prepframe.com/portrait-photography-brief',
  },
  newbornBrief: {
    title: 'Newborn Photography Brief | PrepFrame',
    ogTitle: 'Newborn Photography Brief | PrepFrame',
    description: 'Prepare parents for a smooth newborn session with a simple pre-shoot brief. Collect poses, props, and sibling preferences before they arrive.',
    keywords: 'newborn photography brief, newborn session questionnaire, baby photography client form, newborn shoot planning',
    canonical: 'https://www.prepframe.com/newborn-photography-brief',
  },
  commercialBrief: {
    title: 'Commercial Photography Brief | PrepFrame',
    ogTitle: 'Commercial Photography Brief | PrepFrame',
    description: 'Align deliverables, brand guidelines, and shot lists before any commercial shoot. Collect stakeholder requirements in one brief. No more fragmented emails.',
    keywords: 'commercial photography brief, product photography brief, commercial shoot questionnaire, advertising photography client form',
    canonical: 'https://www.prepframe.com/commercial-photography-brief',
  },
  realEstateBrief: {
    title: 'Real Estate Photography Brief | PrepFrame',
    ogTitle: 'Real Estate Photography Brief | PrepFrame',
    description: 'Know the priority rooms and property highlights before you arrive. Send agents a simple brief link and get everything you need to deliver a complete gallery first time.',
    keywords: 'real estate photography brief, property photography checklist, real estate photo questionnaire, architectural photography brief',
    canonical: 'https://www.prepframe.com/real-estate-photography-brief',
  },
  eventBrief: {
    title: 'Event Photography Brief | PrepFrame',
    ogTitle: 'Event Photography Brief | PrepFrame',
    description: 'Walk into every event with a clear shot list and VIP list. Collect schedule, key people, and restrictions before the day. Spend the event shooting, not scrambling.',
    keywords: 'event photography brief, event shot list, corporate event photography questionnaire, event photographer client form',
    canonical: 'https://www.prepframe.com/event-photography-brief',
  },
  // Blog
  blog: {
    title: 'Photography Blog: Tips, Templates and Guides | PrepFrame',
    description:
      'Photography client questionnaires, wedding intake workflows, and brief templates for Australian photographers. Free resources from PrepFrame.',
    keywords:
      'photography blog, photographer tips, photography client questionnaire, wedding photography workflow, photography questionnaire template',
    canonical: 'https://www.prepframe.com/blog',
    ogType: 'website' as const,
    seoKey: 'blog',
  },
  // App pages - always noindex
  dashboard: {
    title: 'Dashboard - PrepFrame',
    noIndex: true,
  },
  settings: {
    title: 'Settings - PrepFrame',
    noIndex: true,
  },
  auth: {
    title: 'Sign In - PrepFrame',
    noIndex: true,
  },
} as const;
